import { useState, useEffect } from "react";
import axios from "axios";
import {
  Mail,
  Send,
  Clock,
  Bell,
  Save,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  Plus,
  Trash2,
  Users,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem("pricelens_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function Settings() {
  const { isAdmin } = useAuth();
  const [settings, setSettings] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [newEmail, setNewEmail] = useState("");
  const [addingEmail, setAddingEmail] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingReport, setSendingReport] = useState(false);

  useEffect(() => {
    fetchSettings();
    fetchRecipients();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/email`);
      setSettings(response.data);
    } catch (error) {
      console.error("Error fetching settings:", error);
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const fetchRecipients = async () => {
    try {
      const response = await axios.get(`${API}/settings/email-recipients`, {
        headers: getAuthHeaders(),
      });
      setRecipients(response.data.recipients || []);
    } catch (error) {
      console.error("Error fetching recipients:", error);
    }
  };

  const handleAddRecipient = async () => {
    const email = newEmail.trim();
    if (!email) return;
    setAddingEmail(true);
    try {
      await axios.post(
        `${API}/settings/email-recipients`,
        { email },
        { headers: getAuthHeaders() }
      );
      toast.success(`${email} added to daily report recipients`);
      setNewEmail("");
      fetchRecipients();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add recipient");
    } finally {
      setAddingEmail(false);
    }
  };

  const handleDeleteRecipient = async (email) => {
    try {
      await axios.delete(
        `${API}/settings/email-recipients/${encodeURIComponent(email)}`,
        { headers: getAuthHeaders() }
      );
      toast.success(`${email} removed`);
      fetchRecipients();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove recipient");
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await axios.put(`${API}/settings/email`, settings);
      setSettings(response.data);
      toast.success("Settings saved successfully");
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const sendTestReport = async () => {
    setSendingReport(true);
    try {
      const response = await axios.post(
        `${API}/email/send-test`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success(response.data.message || "Test report sent");
    } catch (error) {
      console.error("Error sending report:", error);
      toast.error(error.response?.data?.detail || "Failed to send report");
    } finally {
      setSendingReport(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <Skeleton className="h-10 w-48 mb-2" />
          <Skeleton className="h-5 w-64" />
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div data-testid="settings-page" className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-extrabold tracking-tight text-[#1a1814]">
          Settings
        </h1>
        <p className="text-sm text-[#8a8278]">
          Configure email notifications and price alerts
        </p>
      </div>

      {/* Email Settings */}
      <Card className="bg-white border-[#e0dbd2]">
        <CardHeader className="border-b border-[#e0dbd2]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
              <Mail className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <CardTitle className="text-lg font-extrabold">Email Notifications</CardTitle>
              <CardDescription>
                Configure daily price report emails
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-6 space-y-6">
          {/* Recipient List */}
          <div className="space-y-3">
            <Label className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Daily Report Recipients
            </Label>
            <div data-testid="recipients-list" className="space-y-2 max-w-md">
              {recipients.length === 0 ? (
                <p className="text-xs text-[#8a8278]">No recipients configured yet</p>
              ) : (
                recipients.map((r) => (
                  <div
                    key={r.email}
                    data-testid={`recipient-row-${r.email}`}
                    className="flex items-center justify-between rounded-lg border border-[#e0dbd2] bg-[#f2f0ec] px-3 py-2"
                  >
                    <div>
                      <p className="text-sm font-semibold text-[#1a1814]">{r.email}</p>
                      <p className="text-[10px] text-[#8a8278]">
                        Added by {r.added_by === "system" ? "system" : r.added_by}
                      </p>
                    </div>
                    {isAdmin && (
                      <Button
                        data-testid={`delete-recipient-${r.email}`}
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteRecipient(r.email)}
                        className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))
              )}
            </div>
            {isAdmin ? (
              <div className="flex gap-2 max-w-md">
                <Input
                  data-testid="new-recipient-input"
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleAddRecipient()}
                  placeholder="new.recipient@email.com"
                  className="bg-[#f2f0ec] border-[#e0dbd2]"
                />
                <Button
                  data-testid="add-recipient-btn"
                  onClick={handleAddRecipient}
                  disabled={addingEmail || !newEmail.trim()}
                  className="bg-[#1a1814] hover:bg-[#333] text-white font-bold shrink-0"
                >
                  {addingEmail ? (
                    <RefreshCw className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <Plus className="h-4 w-4 mr-1" /> Add
                    </>
                  )}
                </Button>
              </div>
            ) : (
              <p className="text-xs text-[#8a8278]">
                Only the admin can add or remove recipients
              </p>
            )}
            <p className="text-xs text-[#8a8278]">
              The daily price report is emailed to everyone in this list
            </p>
          </div>

          {/* Daily Report Toggle */}
          <div className="flex items-center justify-between max-w-md">
            <div className="space-y-0.5">
              <Label htmlFor="daily-report">Daily Report</Label>
              <p className="text-xs text-[#8a8278]">
                Receive a daily email with price updates
              </p>
            </div>
            <Switch
              id="daily-report"
              data-testid="daily-report-toggle"
              checked={settings?.send_daily_report || false}
              onCheckedChange={(checked) =>
                setSettings({ ...settings, send_daily_report: checked })
              }
            />
          </div>

          {/* Report Time */}
          <div className="space-y-2">
            <Label htmlFor="report-time" className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Report Time
            </Label>
            <Input
              id="report-time"
              data-testid="report-time-input"
              type="time"
              value={settings?.report_time || "09:00"}
              onChange={(e) =>
                setSettings({ ...settings, report_time: e.target.value })
              }
              className="bg-[#f2f0ec] border-[#e0dbd2] max-w-[150px]"
            />
            <p className="text-xs text-[#8a8278]">
              Time when daily report should be sent (IST)
            </p>
          </div>

          {/* Include Price Drops */}
          <div className="flex items-center justify-between max-w-md">
            <div className="space-y-0.5">
              <Label htmlFor="price-drops">Include Price Drops</Label>
              <p className="text-xs text-[#8a8278]">
                Highlight products with price decreases
              </p>
            </div>
            <Switch
              id="price-drops"
              data-testid="price-drops-toggle"
              checked={settings?.include_price_drops || false}
              onCheckedChange={(checked) =>
                setSettings({ ...settings, include_price_drops: checked })
              }
            />
          </div>

          {/* Include Price Increases */}
          <div className="flex items-center justify-between max-w-md">
            <div className="space-y-0.5">
              <Label htmlFor="price-increases">Include Price Increases</Label>
              <p className="text-xs text-[#8a8278]">
                Highlight products with price increases
              </p>
            </div>
            <Switch
              id="price-increases"
              data-testid="price-increases-toggle"
              checked={settings?.include_price_increases || false}
              onCheckedChange={(checked) =>
                setSettings({ ...settings, include_price_increases: checked })
              }
            />
          </div>

          {/* Minimum Price Change */}
          <div className="space-y-2">
            <Label htmlFor="min-change">Minimum Price Change (%)</Label>
            <Input
              id="min-change"
              data-testid="min-change-input"
              type="number"
              min="0"
              max="100"
              step="0.5"
              value={settings?.min_price_change_percent || 1}
              onChange={(e) =>
                setSettings({
                  ...settings,
                  min_price_change_percent: parseFloat(e.target.value) || 0,
                })
              }
              className="bg-[#f2f0ec] border-[#e0dbd2] max-w-[150px]"
            />
            <p className="text-xs text-[#8a8278]">
              Only include products with at least this % price change
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 pt-4 border-t border-[#e0dbd2]">
            <Button
              data-testid="save-settings-btn"
              onClick={handleSave}
              disabled={saving}
              className="bg-[#1a1814] hover:bg-[#333] text-white font-bold"
            >
              {saving ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Settings
                </>
              )}
            </Button>

            {isAdmin && (
              <Button
                data-testid="send-report-btn"
                variant="outline"
                onClick={sendTestReport}
                disabled={sendingReport}
                className="border-[#e0dbd2] hover:bg-[#f2f0ec]"
              >
                {sendingReport ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Send Test Report
                  </>
                )}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="bg-white border-[#e0dbd2]">
        <CardHeader className="border-b border-[#e0dbd2]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
              <Bell className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <CardTitle className="text-lg font-extrabold">About Daily Reports</CardTitle>
              <CardDescription>
                How the email notification system works
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          <ul className="space-y-3 text-sm text-[#8a8278]">
            <li className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-[#1a7a3c] flex-shrink-0 mt-0.5" />
              <span>
                Daily reports include all your tracked products with current prices, BAU comparison, and recent changes
              </span>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-[#1a7a3c] flex-shrink-0 mt-0.5" />
              <span>
                Products below BAU price are highlighted in red for immediate attention
              </span>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-[#1a7a3c] flex-shrink-0 mt-0.5" />
              <span>
                Price changes are shown with color-coded indicators (green for drops, red for increases)
              </span>
            </li>
            <li className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <span>
                Make sure to sync your products regularly to get the most accurate price data in reports
              </span>
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
